import { FC } from 'react';
import { useSelector } from 'react-redux';
import { activePageSelector } from '../../../../redux/pages/selectors';
import KPIOverview from './KPIOverview.component';
import ESGOverviewPanel from './OverviewPanel.component';
import NonPremiumSubscriptionDisplayMessage, {
  doesClientHavePremiumPackage,
} from '../NonPremiumSubscriptionDisplay/NonPremiumSubscriptionDisplayMessage.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import {
  EsgPages,
  useEsgCodeDescriptions,
  useEsgFundData,
  type EsgCodeDescriptions,
  type EsgPageId,
  type EsgPortfolioSummaryPageModel,
} from '../../../../api/rs-hg/esg';

type IndividualEsgQueryData = {
  pageModel: EsgPortfolioSummaryPageModel;
  rulesData: EsgCodeDescriptions;
};

const individualEsgPageId = (currentPage: string | null): EsgPageId => {
  switch (currentPage) {
    case 'environmental-esg':
      return EsgPages.ENVIRONMENTAL_ESG;
    case 'corporate-governance-esg':
      return EsgPages.CORPORATE_GOVERNANCE_ESG;
    case 'social-esg':
    default:
      return EsgPages.SOCIAL_ESG;
  }
};

const IndividualESGPage: FC<FundInfoComponentProps> = ({
  fundName,
  fundId,
  positionDate,
}) => {
  const currentPage = useSelector(activePageSelector);
  const portfolioSummaryQuery = useEsgFundData(
    fundId,
    positionDate,
    individualEsgPageId(currentPage),
  );
  const codeDescriptionsQuery = useEsgCodeDescriptions();

  return (
    <QueryBoundary<IndividualEsgQueryData>
      isLoading={
        portfolioSummaryQuery.isLoading || codeDescriptionsQuery.isLoading
      }
      isFetching={
        portfolioSummaryQuery.isFetching || codeDescriptionsQuery.isFetching
      }
      isError={portfolioSummaryQuery.isError || codeDescriptionsQuery.isError}
      error={portfolioSummaryQuery.error ?? codeDescriptionsQuery.error}
      data={
        portfolioSummaryQuery.data?.data &&
        Object.keys(portfolioSummaryQuery.data.data).length > 0 &&
        !('no data' in portfolioSummaryQuery.data.data) &&
        codeDescriptionsQuery.data &&
        Object.keys(codeDescriptionsQuery.data).length > 0
          ? {
              pageModel: portfolioSummaryQuery.data,
              rulesData: codeDescriptionsQuery.data,
            }
          : null
      }
      loadingMessages={['Loading ESG Data...', '...']}
      errorMessage="No ESG Data Found"
      noDataMessage="No ESG Data Found"
    >
      {({ pageModel, rulesData }) =>
        doesClientHavePremiumPackage(pageModel.data) ? (
          <>
            <ESGOverviewPanel
              data={pageModel.data}
              currentPage={currentPage}
              fundId={fundId}
              fundName={fundName}
              positionDate={
                pageModel.returned_position_date ??
                pageModel.selected_position_date ??
                positionDate
              }
            />
            <KPIOverview
              data={pageModel.data}
              currentPage={currentPage}
              rulesData={rulesData}
            />
          </>
        ) : (
          <NonPremiumSubscriptionDisplayMessage />
        )
      }
    </QueryBoundary>
  );
};

export default IndividualESGPage;
